import { RelicsToken } from '@apeiron/planet'
import { NextImage } from '@apeiron/temp'
import { Box, styled, Tooltip, TooltipProps } from '@mui/material'
import LockSVG from '@public/icons/icon-lock.svg'
import { getRelicSlotCount } from '@src/util/apeiron/planet'
import { t } from 'i18next'
import React from 'react'

const AGE_BASE = 10

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: -2px;
`

const Square = styled(Box)<{ isDetail: boolean }>`
  width: ${props => (props.isDetail ? '37px' : '23px')};
  height: ${props => (props.isDetail ? '37px' : '23px')};
  border: 1px solid #80807e;
  border-radius: 2px;
  background-color: black;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2px;
`

const Dot = styled('div')`
  width: 5px;
  height: 5px;
  background-color: #c4c4c4;
  border-radius: 50%;
  margin-bottom: 2px;
`

const DotsContainer = styled('div')`
  margin-bottom: 12px;
`

const LockSection = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const RelicsContainer = styled('div')`
  display: flex;
  flex-direction: column-reverse;
`

const TextTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} componentsProps={{ tooltip: { className } }} />
))`
  font-size: 12px;
  font-weight: 400;
  color: '#FFFFFF';
  background-color: #696c6e;
  padding: 8px 12px;
`

const TooltipName = styled('div')`
  font-size: 16px;
  color: #ffffff;
  font-weight: 400;
  line-height: 18px;
  margin-bottom: 5px;
`

const ToolTipDescription = styled('div')`
  font-size: 12px;
  color: #ffffff;
  font-weight: 400;
  line-height: 14px;
`

const LockIcon = styled(NextImage)`
  width: 80%;
  height: 80%;
`

const RelicsToolTipTitle: React.FC<RelicsToolTipTitleProps> = props => {
  const { name, description } = props

  return (
    <>
      <TooltipName>{name}</TooltipName>
      <ToolTipDescription>{description}</ToolTipDescription>
    </>
  )
}

export const RelicsTokenList: React.FC<RelicsTokenListProps> = props => {
  const { age, isDetail = false, relicsTokens } = props

  const relicSlotCount = getRelicSlotCount(age)

  const emptySlotCount = relicSlotCount - relicsTokens.length

  return (
    <Container>
      {isDetail && relicSlotCount < 9 && (
        <LockSection>
          <DotsContainer>
            <Dot />
            <Dot />
            <Dot />
          </DotsContainer>
          <TextTooltip
            title={
              <RelicsToolTipTitle
                name={t('planet.detail.relics_token.tool_tip.lock_slot.title')}
                description={t(
                  'planet.detail.relics_token.tool_tip.lock_slot.description',
                  {
                    age: Math.pow(AGE_BASE, relicSlotCount)
                  }
                )}
              />
            }
          >
            <Square isDetail={isDetail}>
              <LockIcon src={LockSVG} />
            </Square>
          </TextTooltip>
        </LockSection>
      )}
      {emptySlotCount > 0 &&
        Array(emptySlotCount)
          .fill(0)
          // eslint-disable-next-line react/no-array-index-key
          .map((_, i) => <Square key={i} isDetail={isDetail} />)}
      <RelicsContainer>
        {relicsTokens.map(token => {
          return (
            <Square key={token.id} isDetail={isDetail}>
              {isDetail ? (
                <TextTooltip
                  title={
                    <RelicsToolTipTitle
                      name={token.name}
                      description={token.description}
                    />
                  }
                >
                  <NextImage
                    src={token.thumbnail}
                    alt={token.name}
                    width={31}
                    height={31}
                  />
                </TextTooltip>
              ) : (
                <NextImage
                  src={token.thumbnail}
                  alt={token.name}
                  width={23}
                  height={23}
                />
              )}
            </Square>
          )
        })}
      </RelicsContainer>
    </Container>
  )
}

export type RelicsTokenListProps = {
  age: number
  isDetail?: boolean
  relicsTokens: RelicsToken[]
}

type RelicsToolTipTitleProps = {
  name: string
  description: string
}
